import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { fetchAnsRegimes } from '@/services/ans';

const CNPJ_DIGITS = 14;

/**
 * Special regimes (direcao tecnica, direcao fiscal): an instrument, never a sanction.
 *
 * The regimes are listed and counted, per year and per operator, and the
 * page says on its face that a regime was decreed, not adjudicated, and is
 * counted apart from every enforcement figure. An operator's penalty count
 * sits beside its regime so the operator can be opened, not to imply that
 * the regime followed from the penalties.
 */
export function AnsRegimesPage() {
  const { t, i18n } = useTranslation(['ans', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ans', 'regimes'],
    queryFn: fetchAnsRegimes,
  });
  const number = new Intl.NumberFormat(i18n.language);

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ans:regimes.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ans:regimes.subtitle')}</p>
      </header>

      <p className="rounded-md border border-dashed p-3 text-xs text-foreground" role="note">
        {t('ans:regimes.fence')}
      </p>

      <ExplainerCard i18nKey="ans:regimes.explainer" />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/ans/export/regimes" label={t('ans:regimes.export')} />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4" data-shape>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('ans:regimes.shape.title')}
        </h2>
        <Answer answer={data.shape} help={t('ans:regimes.shape.help')}>
          {(s) => (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Figure label={t('ans:regimes.shape.regimes')} value={number.format(s.regimes)} />
                <Figure
                  label={t('ans:regimes.shape.operators')}
                  value={number.format(s.operators)}
                />
                <Figure
                  label={t('ans:regimes.shape.withPenalties')}
                  value={number.format(s.operators_with_penalties)}
                />
                <Figure
                  label={t('ans:regimes.shape.repeat')}
                  value={number.format(s.repeat_operators)}
                />
              </div>
              <p className="text-xs text-muted-foreground" data-by-year>
                {t('ans:regimes.shape.years', {
                  first: s.first_year ?? '—',
                  last: s.last_year ?? '—',
                })}
                {': '}
                {Object.entries(s.by_year)
                  .sort((a, b) => Number(b[0]) - Number(a[0]))
                  .map(([year, n]) => `${year}: ${number.format(n)}`)
                  .join(' · ')}
              </p>
              <p className="text-xs text-muted-foreground" data-by-uf>
                {t('ans:regimes.shape.byUf')}
                {': '}
                {Object.entries(s.by_uf)
                  .sort((a, b) => a[0].localeCompare(b[0]))
                  .map(([uf, n]) => `${uf}: ${number.format(n)}`)
                  .join(' · ')}
              </p>
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="ans:regimes.guide" />
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4" data-regimes>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('ans:regimes.list.title')}
        </h2>
        <Answer answer={data.regimes} help={t('ans:regimes.list.help')}>
          {(rows) => (
            <div className="max-h-[70vh] overflow-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('ans:regimes.list.date')}</th>
                    <th className="px-3 py-2 font-medium">{t('ans:regimes.list.resolution')}</th>
                    <th className="px-3 py-2 font-medium">{t('ans:regimes.list.operator')}</th>
                    <th className="px-3 py-2 font-medium">{t('ans:regimes.list.uf')}</th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:regimes.list.penalties')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, index) => (
                    <tr key={index} className="border-t" data-regime>
                      <td className="px-3 py-2 tabular-nums text-foreground">
                        {r.publication_date ?? r.year ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {t('ans:regimes.list.resolutionValue', {
                          resolution: r.operational_resolution ?? '—',
                        })}
                      </td>
                      <td className="px-3 py-2">
                        {r.operator_cnpj && r.operator_cnpj.length === CNPJ_DIGITS ? (
                          <Link
                            to={`/ans/e/${r.operator_cnpj}`}
                            className="text-primary underline-offset-4 hover:underline"
                          >
                            {r.operator_name ?? r.operator_cnpj}
                          </Link>
                        ) : (
                          <span className="text-foreground">{r.operator_name ?? '—'}</span>
                        )}
                        {r.registro ? (
                          <div className="text-xs text-muted-foreground">{r.registro}</div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{r.uf ?? '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(r.operator_penalties)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Answer>
      </section>
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-muted/30 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-base font-semibold tabular-nums text-foreground">{value}</div>
    </div>
  );
}
